/**
 * How wide the pieces of a formula really are, in the face that will print them.
 *
 * The layout engine needs numbers before it can place anything (see `layout.ts`),
 * and it must not care where they came from. This is where they come from: the
 * platform is asked to lay the strings out — the same text engine, at the same
 * size, in the same face, with the same fallback for a glyph the face does not
 * have — and the widths it reports are handed to the engine.
 *
 * A committed table of font metrics would answer synchronously, and was the
 * obvious alternative. It was not the right one here: this app sets a formula in
 * *the theme's* face, which on a phone is a system stack, and a formula in a
 * school book is full of symbols — `∈`, `⊆`, `∠`, `⊥`, `∇` — that the bundled
 * faces do not carry and the platform substitutes from elsewhere. Nothing written
 * down ahead of time can say what that substitution measures. The probe can: it
 * measures whatever is actually drawn.
 *
 * The cost is one frame. On the first render of a formula the widths are
 * estimates (see `estimateWidth`), the probe reports the real ones, and the
 * layout settles. This is the same bargain the fill-in-the-blank fields already
 * make, and for the same reason: a width that is a few percent out for sixteen
 * milliseconds is a formula that shifts very slightly, while a width that is
 * wrong by a font's worth is a fraction whose bar misses its numerator.
 *
 * The measured widths are cached for the life of the process, keyed by the whole
 * style as well as the string — the same word at script size is a different
 * measurement from the same word at text size.
 *
 * And because that cache is a `Map` at module scope, it has to be *published* to
 * React rather than merely read (below). A render that reads a plain mutable
 * global looks, to anything that reasons about dependencies, like a render that
 * reads nothing — and this app compiles with the React Compiler, which is
 * entitled to memoise on that reading. It did: a probe could be cached into the
 * tree and never leave, so the formula kept drawing its estimates for ever. It
 * was invisible in the studio, where the same formula is laid out more than once
 * — the preview drew, and its fraction bar was simply seven pixels wider than
 * the words under it.
 */
import {useCallback, useMemo, useSyncExternalStore} from 'react'
import {
  type LayoutChangeEvent,
  StyleSheet,
  Text,
  type TextStyle,
  View,
} from 'react-native'

import {type Measure} from './layout'

/** Widths measured on this run, by style and by string. */
const measured = new Map<string, number>()

/**
 * The measurement cache, published as an external store.
 *
 * React has one first-class way to make a value outside the tree a real
 * dependency of a render, and this is it: a snapshot it can compare, and a
 * subscription that tells it when to look again. `widths` is the live `Map` —
 * stable identity, so a render reads whatever has arrived by the time it runs —
 * while the *snapshot object* is replaced on every measurement, which is the
 * change React re-renders for.
 */
type Measurements = {widths: Map<string, number>}
let snapshot: Measurements = {widths: measured}
const listeners = new Set<() => void>()

function subscribe(listener: () => void): () => void {
  listeners.add(listener)
  return () => {
    listeners.delete(listener)
  }
}

const getSnapshot = (): Measurements => snapshot

/** Publish a landed measurement. */
function noteMeasurement(): void {
  snapshot = {widths: measured}
  for (const listener of listeners) listener()
}

/** One piece of a formula that has to be measured: a run, at one size and slant. */
export type FormulaLeaf = {
  text: string
  italic: boolean
  size: number
}

/**
 * The average glyph width assumed before anything has been measured, in ems.
 *
 * Maths is set from a narrower alphabet than running text — digits, signs and
 * italic letters — so this is smaller than the estimate the reading blocks use
 * for prose, and deliberately so: a page of `+` signs measured at a prose average
 * would reserve a third more room than it needs.
 *
 * A number the app owns, and one that is *measured* rather than chosen: it is the
 * mean advance the bundled face actually gives the characters a formula is
 * written with — 0.618 em over 187 of them — and the test beside this module
 * re-derives that from the font file and fails if the two drift apart.
 */
export const ESTIMATED_ADVANCE = 0.62

/** What a string would take, on the average-glyph assumption. */
export function estimateWidth(text: string, size: number): number {
  return text.length * size * ESTIMATED_ADVANCE
}

/** The key a leaf is measured and cached under. */
function leafKey(font: TextStyle, leaf: FormulaLeaf): string {
  return [
    font.fontFamily ?? '',
    font.fontWeight ?? '',
    leaf.italic ? 'i' : 'n',
    leaf.size,
    leaf.text,
  ].join('\u0000')
}

export type TexWidths = {
  /** The widths the platform has reported, for `texMeasure` to read. */
  widths: Map<string, number>
  /** The probe to render once, inside the block being laid out. */
  probe: React.ReactNode
}

export function useTexWidths(
  font: TextStyle,
  leaves: readonly FormulaLeaf[],
): TexWidths {
  /*
   * The snapshot is taken, not just read: it is the object React replaces when a
   * measurement lands, and it is the value the memo below has to depend on. The
   * `Map` inside it never changes identity — a landed width is written into it —
   * so keying on the map would memoise the first, empty answer and keep the probe
   * in the tree for ever, which is exactly the bug this store exists to prevent.
   */
  const store = useSyncExternalStore(subscribe, getSnapshot, getSnapshot)

  const {widths, pending} = useMemo(() => {
    const seen = new Set<string>()
    const found = new Map<string, number>()
    const missing: FormulaLeaf[] = []
    for (const leaf of leaves) {
      if (!leaf.text) continue
      const key = leafKey(font, leaf)
      if (seen.has(key)) continue
      seen.add(key)
      const width = store.widths.get(key)
      if (width !== undefined) found.set(key, width)
      else missing.push(leaf)
    }
    return {widths: found, pending: missing}
  }, [font, leaves, store])

  const report = useCallback(
    (leaf: FormulaLeaf, width: number) => {
      const key = leafKey(font, leaf)
      if (!Number.isFinite(width) || width <= 0) return
      if (measured.get(key) !== width) measured.set(key, width)
      /*
       * Publish even when the width was already known, which looks redundant and
       * is not. A probe is only rendered for a leaf that *this* render found
       * missing, so a report can arrive for a width another formula measured
       * first — and staying quiet in that case would leave the probe mounted and
       * the drawing standing on estimates for good. A probe cannot report twice
       * — once the cache holds it the probe is gone — so this re-renders at most
       * once more per probe, not in a loop.
       */
      noteMeasurement()
    },
    [font],
  )

  const probe = pending.length ? (
    /*
     * Out of the page's flow, invisible and silent, and *laid out*: `opacity: 0`
     * rather than `display: none`, because a hidden element has no width to
     * report. It is one row and it is `flex-wrap`, so a long formula's probe does
     * not become the overflow a layout test finds.
     */
    <View aria-hidden pointerEvents="none" style={styles.probe}>
      {pending.map(leaf => (
        <Text
          key={leafKey(font, leaf)}
          style={[
            font,
            {
              fontSize: leaf.size,
              fontStyle: leaf.italic ? 'italic' : 'normal',
            },
          ]}
          onLayout={(event: LayoutChangeEvent) =>
            report(leaf, event.nativeEvent.layout.width)
          }>
          {leaf.text}
        </Text>
      ))}
    </View>
  ) : null

  return {widths, probe}
}

/**
 * A `Measure` backed by what the platform has reported.
 *
 * A string nothing has measured yet falls back to the estimate *for that string
 * only*, so the first frame is drawn in approximately the right places instead of
 * collapsing.
 */
export function texMeasure(
  font: TextStyle,
  widths: Map<string, number>,
): Measure {
  return (text, {italic, size}) =>
    widths.get(leafKey(font, {text, italic, size})) ?? estimateWidth(text, size)
}

const styles = StyleSheet.create({
  probe: {
    position: 'absolute',
    top: 0,
    left: 0,
    width: '100%',
    flexDirection: 'row',
    flexWrap: 'wrap',
    alignItems: 'flex-start',
    opacity: 0,
  },
})
